import { ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { VideoRecommendationStatus } from '../../use-video-recommendation';
import { VideoPlayer } from '@/components/shared/video-player';
import type { VideoRecommendationCard } from '@/data/agent/video-recommendation';
import { lineClamp } from '@/utils/line-clamp';

interface RecommendationResultProps {
  status: VideoRecommendationStatus;
  video: VideoRecommendationCard | null;
  textMessage: string;
  errorMessage: string;
  onRegenerate: () => void;
}

const SectionTitle = () => (
  <Typography variant="h2">
    Aqui está a nossa recomendação diária para você:
  </Typography>
);

const VideoResult = ({
  video,
  onRegenerate,
}: {
  video: VideoRecommendationCard;
  onRegenerate: () => void;
}) => {
  const [expanded, setExpanded] = useState(true);

  return (
    <Accordion
      expanded={expanded}
      onChange={(_, next) => setExpanded(next)}
      disableGutters
      elevation={0}
      square
      sx={{
        width: '100%',
        p: '0 !important',
        bgcolor: 'transparent',
        borderRadius: 0,
        '&::before': { display: 'none' },
        '& .MuiAccordionSummary-root, & .MuiAccordionSummary-root.Mui-expanded':
          { p: 0, minHeight: 'unset' },
        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
          { m: 0 },
        '& .MuiAccordionDetails-root': { p: 0, pt: 2 },
      }}
    >
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Box flex={1}>
          <SectionTitle />
          <Typography
            color="text.secondary"
            sx={expanded ? undefined : lineClamp(1)}
          >
            {video.reason}
          </Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        <Stack spacing={2} alignItems="flex-start">
          <Box width="100%">
            <VideoPlayer url={video.video_url} fullWidth />
          </Box>
          <Button variant="outlined" onClick={onRegenerate}>
            Refazer recomendação
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  );
};

const TextResult = ({ message }: { message: string }) => {
  const navigate = useNavigate();

  return (
    <Stack spacing={2} alignItems="flex-start">
      <SectionTitle />
      <Typography>{message}</Typography>
      <Button variant="contained" onClick={() => navigate('/meu-perfil')}>
        Ver perfil
      </Button>
    </Stack>
  );
};

const ErrorResult = ({
  message,
  onRegenerate,
}: {
  message: string;
  onRegenerate: () => void;
}) => (
  <Stack spacing={2} alignItems="flex-start">
    <SectionTitle />
    <Typography color="error">{message}</Typography>
    <Button variant="outlined" onClick={onRegenerate}>
      Tentar novamente
    </Button>
  </Stack>
);

export const RecommendationResult = ({
  status,
  video,
  textMessage,
  errorMessage,
  onRegenerate,
}: RecommendationResultProps) => {
  if (status === VideoRecommendationStatus.SuccessVideo && video) {
    return <VideoResult video={video} onRegenerate={onRegenerate} />;
  }

  if (status === VideoRecommendationStatus.SuccessText) {
    return <TextResult message={textMessage} />;
  }

  return (
    <Box>
      <ErrorResult message={errorMessage} onRegenerate={onRegenerate} />
    </Box>
  );
};
